import _cloneDeep from 'lodash/cloneDeep';
import _groupBy from 'lodash/groupBy';
import _isNil from 'lodash/isNil';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import EntitySelectionPopup from '@Components/MapComponents/MapLibre/EntitySelectionPopup';
import MapComponent from '@Components/MapComponents/MapLibre/MapLibreMapComponent';
import {
    IPopupDetails,
    LayerType,
    getLayerId,
    getWfsData,
    mapWfsData,
} from '@Components/MapComponents/MapLibre/Utils';
import EntitiesApi from '@Data/entities/Api';
import { IEntity } from '@Data/entities/Interfaces';
import { selectedEntityState } from '@Data/entities/Reducer';
import FoldersApi from '@Data/folders/Api';
import { IFolder } from '@Data/folders/Interfaces';
import { refreshIndexState, visibleFoldersState } from '@Data/folders/Reducer';
import { selectedProjectState } from '@Data/projects/Reducer';
import { viewStateState } from '@Data/workbenches/Reducer';
import { KoruRoutes } from '@Services/navigation/Paths';

const ProjectMap = () => {
    const navigate = useNavigate();
    const { organizationId, teamId, projectId, folderIds } = useParams();

    const selectedEntity: IEntity = useSelector(selectedEntityState);
    const visibleFolders: Array<IFolder> = useSelector(visibleFoldersState);
    const selectedProject = useSelector(selectedProjectState);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [baseLayerId, setBaseLayerId] = useState<string>('');
    const [sourceData, setSourceData] = useState<any>(null);
    const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
    const [popupDetails, setPopupDetails] = useState<IPopupDetails>({
        coordinates: [0, 0],
        entityVersionsByClientId: null,
    });
    const viewState = useSelector(viewStateState);
    const folderRefreshIndex = useSelector(refreshIndexState);

    useEffect(() => {
        EntitiesApi.setSelectedEntityVersion(null);
        FoldersApi.setSelectedFolder(null);
        setIsPopupOpen(false);
        if (_isNil(visibleFolders) || visibleFolders.length === 0) {
            setSourceData(null);
        } else {
            setBaseLayerId(Math.random().toString(32));
            const getData = async () => {
                setIsLoading(true);
                const response = await getWfsData(
                    visibleFolders.map((f) => f.folder_id),
                    selectedProject.project_id
                );
                if (response.data) {
                    setIsLoading(false);
                    console.log(response.data);

                    const features = mapWfsData(response.data.features);

                    const responseData = _cloneDeep(response.data);
                    responseData.features = features;

                    setSourceData(responseData);
                }
            };
            getData();
        }
    }, [visibleFolders, folderRefreshIndex]);

    const handleFeatureClick = (features) => {
        const pointFeatures = features.filter(
            (f) => f.layer.id === getLayerId(baseLayerId, LayerType.POINT)
        );
        if (pointFeatures.length > 1) {
            const grouped = _groupBy(
                pointFeatures,
                (f) => f.properties.client_id
            );
            setPopupDetails({
                coordinates: pointFeatures[0].geometry.coordinates,
                entityVersionsByClientId: grouped,
            });
            setIsPopupOpen(true);
        } else if (pointFeatures.length > 0) {
            handleSelectEntityVersion(
                pointFeatures[0].properties.entity_version_id
            );
        }
    };

    const handleSelectEntityVersion = (entityVersionId: string) => {
        setIsLoading(true);
        EntitiesApi.getEntityVersionDetails(
            entityVersionId,
            (entityVersion) => {
                setIsLoading(false);
                EntitiesApi.setSelectedEntityVersion(entityVersion);
                FoldersApi.setSelectedFolder(entityVersion.folder[0]);

                navigate(
                    KoruRoutes.project.getUrl(
                        organizationId,
                        teamId,
                        parseInt(projectId),
                        folderIds,
                        entityVersion.entity_version_id
                    )
                );
            }
        );
    };

    return (
        <MapComponent
            sourceData={sourceData}
            selectedEntity={selectedEntity}
            onFeatureClick={handleFeatureClick}
            isPopupOpen={isPopupOpen}
            isLoading={isLoading}
            baseLayerId={baseLayerId}
            popupComponent={
                <EntitySelectionPopup
                    onSelectEntityVersion={handleSelectEntityVersion}
                    coordinates={popupDetails.coordinates}
                    onClose={() => setIsPopupOpen(false)}
                    entityVersionsByClientId={
                        popupDetails.entityVersionsByClientId
                    }
                    visibleFolders={visibleFolders}
                />
            }
            initialViewState={viewState}
        />
    );
};

export default ProjectMap;
